import React, { useCallback, useState, useRef, useMemo } from 'react';
import { localPoint } from '@visx/event';
import { scaleTime } from '@visx/scale';
import { useTooltipInPortal, defaultStyles as defaultTooltipStyle } from '@visx/tooltip';
import Indicator from './Indicator';
import { datetimeString, utcToSite } from '../../../datetimeUtils';
import { DropdownMenu } from '../../DropdownMenu';

// Common timeline height constants
export function getTimelineConstants() {
  const USER_EVENT_HEIGHT = 25;
  return {
    USER_EVENT_HEIGHT,
    PADDING_BOTTOM: USER_EVENT_HEIGHT,
    IOT_EVENT_HEIGHT: USER_EVENT_HEIGHT * 4,
  };
}

// Common tooltip portal configuration
export const TOOLTIP_PORTAL_CONFIG = {
  detectBounds: true,
  scroll: true,
};

export const defaultTooltipStyles = defaultTooltipStyle;

export function getComponentNameForDtmpEvent(event, staticComponents) {
  if (event.deviatingReferenceDesignation && staticComponents) {
    const component = staticComponents.find(
      (c) => c.itemDesignation === event.deviatingReferenceDesignation
    );
    return component?.name || '';
  }
  return '';
}

/**
 * Hook to create a time scale for timeline x-axis
 * @param {Array} dateDomain - [startTime, endTime] for the domain
 * @param {number} width - Total width of the timeline
 * @param {object} margin - Margin object with left and right properties
 * @returns {function} The x-axis scale function
 */
export function useTimeScale(dateDomain, width, margin) {
  return useMemo(
    () =>
      scaleTime({
        domain: [dateDomain[0], dateDomain[1]],
        range: [margin.left, width - margin.right],
        clamp: true,
      }),
    [dateDomain, width, margin.left, margin.right]
  );
}

/**
 * Hook for tooltip portal with standard configuration
 */
export function useTimelineTooltipPortal() {
  return useTooltipInPortal(TOOLTIP_PORTAL_CONFIG);
}

export function formatYTick(value, eventTypes = []) {
  if (!value || value === 'default') {
    return '';
  }
  if (value === 'iot') {
    return 'IOT events';
  }
  const eventType = eventTypes.find(({ id }) => id === value);
  if (eventType?.name) {
    return eventType.name.charAt(0).toUpperCase() + eventType.name.slice(1);
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const EventPicker = (properties) => {
  const {
    events,
    onSelect,
    position: { placeAbove = false, alignLeft = true, top = 0, left = 0 },
    onClose,
    timezone,
  } = properties;
  let translate = 'translate(0%, 2%)';
  if (placeAbove || !alignLeft) {
    translate = `translate(${alignLeft ? '0%' : '-100%'}, ${placeAbove ? '-102%' : '2%'})`;
  }

  const buildEventOptions = () =>
    events.map((event) => {
      const { severity, timestamp, id } = event;
      return {
        component: (
          <div
            key={id}
            className="iot-event-option"
            onClick={() => onSelect(event)}
          >
            <Indicator severity={severity} />
            <div>{severity}</div>
            <div>{datetimeString(utcToSite(timestamp, timezone), timezone, true)}</div>
          </div>
        ),
      };
    });

  return (
    <DropdownMenu
      trigger={<div></div>}
      menuOptions={buildEventOptions()}
      className="event-picker"
      onMenuClose={onClose}
      menuIsOpen={true}
      autoFocus
      styles={{
        dropdownIndicator: { padding: '0 0.5rem', filter: 'none', visibility: 'hidden' },
        option: { cursor: 'pointer' },
        container: { position: 'initial' },
        menu: {
          left: `${left}px`,
          top: `${top}px`,
          marginTop: '0',
          marginBottom: '0',
          transform: translate,
          minWidth: 'auto',
        },
        menuList: { maxHeight: '11rem' },
      }}
    />
  );
};

export function useEventPicker(onClose, dependencies) {
  const [view, setView] = useState(null);
  const openModal = useCallback(
    (properties = {}) =>
      new Promise((resolve) => {
        const handleOnClose = () => {
          if (onClose) {
            onClose();
          }
          if (properties.onClose) {
            properties.onClose();
          }
          setView(null);
          resolve();
        };
        setView(
          <EventPicker
            {...properties}
            onClose={handleOnClose}
          />
        );
      }),
    dependencies
  );
  return [view, openModal];
}

export const getGroupedUserEventOptions = (userEventsArray) => {
  const groupedTypes = [
    { label: 'EVENTS', options: [] },
    { label: 'TASKS', options: [] },
  ];

  for (const eventType of userEventsArray) {
    if (eventType.task) {
      groupedTypes[1].options.push({ value: eventType.id, label: eventType.name });
    } else {
      groupedTypes[0].options.push({ value: eventType.id, label: eventType.name });
    }
  }

  return [
    {
      ...groupedTypes[0],
      options: groupedTypes[0].options.toSorted(({ value: a }, { value: b }) => a.localeCompare(b)),
    },
    {
      ...groupedTypes[1],
      options: groupedTypes[1].options.toSorted(({ value: a }, { value: b }) => a.localeCompare(b)),
    },
  ];
};

/**
 * Hook to handle drag-to-zoom functionality for timeline components
 * @param {object} xScale - The x-axis scale function
 * @param {boolean} hitMaxZoom - Whether the maximum zoom level has been reached
 * @param {function} onZoomChange - Callback to handle zoom changes
 * @returns {object} Object containing zoomRef and drag handlers (onDragStart, onDrag, onDragEnd)
 */
export function useTimelineDragZoom(xScale, hitMaxZoom, onZoomChange) {
  const dragState = useRef({
    isDragging: false,
    startX: 0,
  });
  const zoomReference = useRef();

  const onDragStart = useCallback((event) => {
    dragState.current.isDragging = true;
    const { x } = localPoint(event);
    dragState.current.startX = x;
  }, []);

  const onDrag = useCallback(
    (event) => {
      if (dragState.current.isDragging) {
        const { x: x2 } = localPoint(event);
        const x = dragState.current.startX;
        const min = Math.max(xScale.range()[0], Math.min(x, x2));
        const max = Math.min(xScale.range()[1], Math.max(x, x2));
        if (zoomReference.current) {
          const element = zoomReference.current;
          element.setAttribute('x', min);
          element.setAttribute('width', Math.max(0, max - min));
        }
      }
    },
    [xScale]
  );

  const onDragEnd = useCallback(
    (event) => {
      if (dragState.current.isDragging) {
        const { x: endX } = localPoint(event);
        dragState.current.isDragging = false;
        if (endX !== dragState.current.startX && !hitMaxZoom) {
          const cx1 = Math.min(endX, dragState.current.startX);
          const cx2 = Math.max(endX, dragState.current.startX);
          onZoomChange([xScale.invert(cx1).getTime(), xScale.invert(cx2).getTime(), null]);
        }
        if (zoomReference.current) {
          const element = zoomReference.current;
          element.setAttribute('x', 0);
          element.setAttribute('width', 0);
        }
      }
    },
    [xScale, hitMaxZoom, onZoomChange]
  );

  return {
    zoomReference,
    onDragStart,
    onDrag,
    onDragEnd,
  };
}

export default {
  formatYTick,
  useEventPicker,
  getGroupedUserEventOptions,
  useTimelineDragZoom,
  useTimeScale,
  useTimelineTooltipPortal,
  getTimelineConstants,
  defaultTooltipStyles,
};
